import { api } from '../api/client';

import { API_ITEMS_COUNT_URL, API_ITEMS_URL } from '../utils';

/**
 * One cell of the item table, saved through the FORM's own write path —
 * `PUT /api/items/{id}` — so every rule the form meets on the server (grants,
 * the lock, required and requiredWhen, driven and sequenced values, lookup
 * scope, attestation) is met here too. Two differences, both on the wire:
 *
 *   - `merge: true`: the table sends the one value it changed. The PUT
 *     merges by default now (delete audit F6: an omitted key keeps its
 *     stored value; only an explicit `merge: false` replaces), so this is
 *     belt and braces — a one-cell save must never erase the rest of the
 *     row, whatever the default.
 *   - `ifUpdatedAt`: the row's `updatedAt` as the table read it. A list is
 *     read once and edited for as long as it is on screen, so without it a
 *     cell save would silently roll back whatever a colleague saved in
 *     between. A stale row is 409 `stale_item` with the item as it stands.
 *
 * `config` carries the attestation header on the retry after a 428.
 */
export async function saveItemCells(itemId, elements, { ifUpdatedAt = null, config = undefined } = {}) {
	const payload = { elements, merge: true };
	if (ifUpdatedAt) payload.ifUpdatedAt = ifUpdatedAt;
	const { data } = await api.put(`${API_ITEMS_URL}/${itemId}`, payload, config);
	return data;
}

export const isStaleItem = error => error?.response?.status === 409 && error?.response?.data?.code === 'stale_item';

/**
 * Move an item in a MANUAL ORDER (KPR-1396): `POST /api/items/{id}/rank`.
 * `after` is the item it goes right after, `before` the item it goes right
 * before (one is enough), or `to: 'top' | 'bottom'`. Answers
 * `{ item, element, rank, renumbered }`. A concurrent move is 409 `stale_item`
 * (the item as it stands) or `rank_stale` (the neighbours moved): reload.
 * `config` carries the attestation header on the retry after a 428.
 */
export async function rankItem(itemId, { element, after = null, before = null, to = null, ifUpdatedAt = null } = {}, config = undefined) {
	const payload = { element };
	if (to) payload.to = to;
	if (after) payload.after = after;
	if (before) payload.before = before;
	if (ifUpdatedAt) payload.ifUpdatedAt = ifUpdatedAt;
	const { data } = await api.post(`${API_ITEMS_URL}/${itemId}/rank`, payload, config);
	return data;
}

/** A 409 that means the list this person is looking at is out of date: reload it. */
export const isStaleOrder = error => error?.response?.status === 409 && ['stale_item', 'rank_stale'].includes(error?.response?.data?.code);

/**
 * An item's HAND-APPLIED tags, saved through the form's write path —
 * `PUT /api/items/{id}` with `{ tagIds }` and nothing else (keepr-api §
 * Tags on items). The whole list goes, as PUT takes it; `elements` is left
 * out, so the stored map stands (merge or replace alike). `ifUpdatedAt` is
 * the item as the surface read it: a list is read once and tagged for as
 * long as it is on screen, so a stale row is 409 `stale_item` with the item
 * as it stands (hooks/useItemTagWriter re-applies its change once on that).
 * The answer is the item with `tagTitles` (and `warnings` — `tag_dropped` —
 * when a tag deleted since was left off). `config` carries the attestation
 * header on the retry after a 428.
 */
export async function saveItemTags(itemId, tagIds, { ifUpdatedAt = null, config = undefined } = {}) {
	const payload = { tagIds: Array.isArray(tagIds) ? tagIds : [] };
	if (ifUpdatedAt) payload.ifUpdatedAt = ifUpdatedAt;
	const { data } = await api.put(`${API_ITEMS_URL}/${itemId}`, payload, config);
	return data;
}

/**
 * `GET /api/items/count` — how many items a list query matches, with the
 * same filter params the list takes (`collection_id`, `card_id`, `q`,
 * `review`, `includeLinked`…). `{ count }` in the body, mirrored in
 * `X-Total-Count`; a number, or null when the answer is not one.
 */
export async function countItems(params) {
	const { data } = await api.get(API_ITEMS_COUNT_URL, { params });
	const count = Number(data?.count);
	return Number.isFinite(count) && count >= 0 ? count : null;
}

/** `GET /api/items/{id}` — one item as the caller may read it. */
export async function getItem(itemId) {
	const { data } = await api.get(`${API_ITEMS_URL}/${itemId}`);
	return data;
}
